import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { INotificationFrequency, IUserNotificationTypeSetting, UserNotificationService } from '@nha/api/notifications';
import { BusinessSystem } from '@nha/enums';
import { NHADialogComponent } from '../../../dialog/dialog.component';
import { NHANoDataMessageComponent } from '../../../no-data-message/no-data-message.component';
import { NHASaveStatusComponent } from '../../../save-status/save-status.component';
import { SaveStatus } from '../../../save-status/save-status.enum';
import { NHASearchBoxComponent } from '../../../search-box/search-box.component';
import { NHASkeletonComponent } from '../../../skeleton/skeleton.component';
import { NHAAlertBoxComponent } from '../../../alert-box/nha-alert-box.component';
import { NHASnackbarService } from '../../../snack-bar/snack-bar.service';
import { NHATestDirective } from '../../../../directives/test/test.directive';
import { INotificationAppPreferences, INotificationTypePreference } from '../../interfaces';
import { NotificationMenuStore } from '../../state';
import { buildAppDisplay } from '../../functions/app-display.function';

const SAVE_DEBOUNCE_MS = 600;

export interface INotificationSettingsDialogData {
	businessSystemID: number;
}

@Component({
	selector: 'nha-notification-settings-dialog',
	templateUrl: './notification-settings-dialog.component.html',
	styleUrls: ['./notification-settings-dialog.component.scss'],
	encapsulation: ViewEncapsulation.None,
	imports: [
		NHAAlertBoxComponent,
		NHADialogComponent,
		NHANoDataMessageComponent,
		NHASaveStatusComponent,
		NHASearchBoxComponent,
		NHASkeletonComponent,
		NHATestDirective,
		MatButtonModule,
		MatCheckboxModule,
		MatFormFieldModule,
		MatIconModule,
		MatSelectModule,
		MatSlideToggleModule
	]
})
export class NHANotificationSettingsDialogComponent implements OnInit {
	private _dialogRef = inject(MatDialogRef<NHANotificationSettingsDialogComponent>);
	private _dialogData = inject<INotificationSettingsDialogData>(MAT_DIALOG_DATA, { optional: true });
	private _service = inject(UserNotificationService);
	private _snackbarService = inject(NHASnackbarService);
	private _store = inject(NotificationMenuStore);

	private _businessSystemID: number = this._dialogData?.businessSystemID ?? BusinessSystem.MyNHA;
	private _saveTimer: number = null;
	/** Types edited since the last save fired, so only touched rows are posted. */
	private _pendingTypeIDs = new Set<number>();

	preferences = signal<INotificationAppPreferences[]>([]);
	frequencies = signal<INotificationFrequency[]>([]);
	searchText = signal<string>('');
	saveStatus = signal<SaveStatus>(null);
	isLoading = signal<boolean>(true);
	hasError = signal<boolean>(false);

	private _expandedAppIDs = signal<number[]>([]);

	filteredPreferences = computed(() => {
		const search = this.searchText()?.trim().toLowerCase();

		if (!search) {
			return this.preferences();
		}

		return this.preferences()
			.map((app) => ({
				...app,
				types: app.types.filter((t) => t.name.toLowerCase().includes(search) || t.description?.toLowerCase().includes(search))
			}))
			.filter((app) => app.types.length > 0);
	});

	ngOnInit(): void {
		this.load();
	}

	load(): void {
		this.isLoading.set(true);
		this.hasError.set(false);

		forkJoin({
			applications: this._service.getBusinessApplications(this._businessSystemID),
			types: this._service.getNotificationTypes(this._businessSystemID),
			frequencies: this._service.getNotificationFrequencies(),
			settings: this._service.getSettings(this._businessSystemID)
		}).subscribe({
			next: ({ applications, types, frequencies, settings }) => {
				const settingsByTypeID = new Map(settings.map((s) => [s.notificationTypeID, s]));

				const preferences = applications
					.map((application) => ({
						app: buildAppDisplay(application),
						types: types
							.filter((t) => t.businessApplicationID === application.businessApplicationID && !t.isDisabled)
							.map((type) => {
								// a user with no saved row inherits the type's own delivery defaults
								const setting = settingsByTypeID.get(type.notificationTypeID);

								return {
									notificationTypeID: type.notificationTypeID,
									userNotificationTypeSettingID: setting?.userNotificationTypeSettingID ?? 0,
									name: type.typeName,
									description: type.description,
									enabled: setting?.isActive ?? true,
									inApp: setting?.receiveInApp ?? type.isInApp,
									email: setting?.receiveEmail ?? type.isEmail,
									push: setting?.receivePush ?? type.isPush,
									sms: setting?.receiveSMS ?? type.isSMS,
									notificationFrequencyID: setting?.notificationFrequencyID ?? type.notificationFrequencyID,
									canOverrideFrequency: type.userCanOverrideFrequency
								};
							})
					}))
					.filter((app) => app.types.length > 0);

				this.frequencies.set(frequencies);
				this.preferences.set(preferences);
				this._expandedAppIDs.set(preferences.length ? [preferences[0].app.businessApplicationID] : []);
				this.isLoading.set(false);
			},
			error: () => {
				this.hasError.set(true);
				this.isLoading.set(false);
			}
		});
	}

	isExpanded(businessApplicationID: number): boolean {
		return !!this.searchText()?.trim() || this._expandedAppIDs().includes(businessApplicationID);
	}

	toggleSection(businessApplicationID: number): void {
		this._expandedAppIDs.update((ids) =>
			ids.includes(businessApplicationID) ? ids.filter((id) => id !== businessApplicationID) : [...ids, businessApplicationID]
		);
	}

	summary(app: INotificationAppPreferences): string {
		const enabledCount = app.types.filter((t) => t.enabled).length;
		return `${app.types.length} ${app.types.length === 1 ? 'type' : 'types'} · ${enabledCount} on`;
	}

	frequencyName(notificationFrequencyID: number): string {
		return this.frequencies().find((f) => f.notificationFrequencyID === notificationFrequencyID)?.frequencyName ?? '';
	}

	updateType(businessApplicationID: number, notificationTypeID: number, patch: Partial<INotificationTypePreference>): void {
		const previous = this.preferences();

		this.preferences.set(
			previous.map((app) =>
				app.app.businessApplicationID !== businessApplicationID
					? app
					: { ...app, types: app.types.map((t) => (t.notificationTypeID !== notificationTypeID ? t : { ...t, ...patch })) }
			)
		);

		this._pendingTypeIDs.add(notificationTypeID);
		this.queueSave(previous);
	}

	done(): void {
		this._dialogRef.close();
	}

	private queueSave(previous: INotificationAppPreferences[]): void {
		this.saveStatus.set(SaveStatus.Saving);
		window.clearTimeout(this._saveTimer);

		this._saveTimer = window.setTimeout(() => this.save(previous), SAVE_DEBOUNCE_MS);
	}

	private save(previous: INotificationAppPreferences[]): void {
		const pending = new Set(this._pendingTypeIDs);
		this._pendingTypeIDs.clear();

		const changed = this.preferences()
			.flatMap((app) => app.types)
			.filter((type) => pending.has(type.notificationTypeID));

		if (!changed.length) {
			this.saveStatus.set(SaveStatus.Saved);
			return;
		}

		this._service.saveSettings(changed.map((type) => this.toSetting(type))).subscribe({
			next: () => {
				this.saveStatus.set(SaveStatus.Saved);
				this._store.settingsChanged();

				// inserts come back without their new IDs, and the unique index on
				// (NotificationTypeID, StaffID) means posting 0 twice would fail — so re-read them
				if (changed.some((type) => type.userNotificationTypeSettingID === 0)) {
					this.syncSettingIDs();
				}

				this._snackbarService
					.open('Preferences updated', 'Undo', 'primary', { duration: 4000 })
					.onAction()
					.subscribe(() => this.undo(previous, changed));
			},
			error: () => {
				this.saveStatus.set(null);
				this.preferences.set(previous);
			}
		});
	}

	private undo(previous: INotificationAppPreferences[], changed: INotificationTypePreference[]): void {
		const changedTypeIDs = new Set(changed.map((t) => t.notificationTypeID));
		const restored = previous.flatMap((app) => app.types).filter((type) => changedTypeIDs.has(type.notificationTypeID));

		this.preferences.set(previous);
		this.saveStatus.set(SaveStatus.Saving);

		// the rows exist server-side now, so replay the prior values using the current IDs
		this._service.saveSettings(restored.map((type) => this.toSetting(type, this.currentSettingID(type.notificationTypeID)))).subscribe({
			next: () => {
				this.saveStatus.set(SaveStatus.Saved);
				this._store.settingsChanged();
			},
			error: () => this.saveStatus.set(null)
		});
	}

	private currentSettingID(notificationTypeID: number): number {
		return (
			this.preferences()
				.flatMap((app) => app.types)
				.find((t) => t.notificationTypeID === notificationTypeID)?.userNotificationTypeSettingID ?? 0
		);
	}

	private syncSettingIDs(): void {
		this._service.getSettings(this._businessSystemID).subscribe((settings) => {
			const idsByTypeID = new Map(settings.map((s) => [s.notificationTypeID, s.userNotificationTypeSettingID]));

			this.preferences.update((apps) =>
				apps.map((app) => ({
					...app,
					types: app.types.map((type) => ({
						...type,
						userNotificationTypeSettingID: idsByTypeID.get(type.notificationTypeID) ?? type.userNotificationTypeSettingID
					}))
				}))
			);
		});
	}

	private toSetting(type: INotificationTypePreference, overrideSettingID?: number): IUserNotificationTypeSetting {
		return {
			userNotificationTypeSettingID: overrideSettingID ?? type.userNotificationTypeSettingID,
			notificationTypeID: type.notificationTypeID,
			notificationFrequencyID: type.notificationFrequencyID,
			isActive: type.enabled,
			receiveInApp: type.inApp,
			receivePush: type.push,
			receiveSMS: type.sms,
			receiveEmail: type.email
		};
	}
}
